import { useQuery } from '@apollo/client';
import {
    faBolt,
    faClockRotateLeft,
    faDatabase,
    faFlask,
    faGear,
    faGrid2,
    faPlug,
} from '@fortawesome/pro-regular-svg-icons';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useParams } from 'react-router-dom';

import { StatusDot, StatusLabel } from '../../../chat/components/automations/RunStatusBadge.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';
import Layout from '../../../core/components/Layout.tsx';
import Loader from '../../../core/components/Loader.tsx';
import Page from '../../../core/components/Page';
import SecondaryNavigation from '../../../core/components/SecondaryNavigation.tsx';
import { useApplicationContext } from '../../../core/contexts/ApplicationContext.tsx';
import { url } from '../../../core/utils/link.ts';
import { canManageAgents, canSeeTestingDocuments } from '../../../users/utils.ts';
import TestAssistantDrawer from '../components/activity/TestAssistantDrawer.tsx';
import AssistantOverview from '../components/overview/AssistantOverview.tsx';
import AssistantActivityTab from '../components/tabs/AssistantActivityTab.tsx';
import AssistantIntegrationsTab from '../components/tabs/AssistantIntegrationsTab.tsx';
import AssistantKnowledgeTab from '../components/tabs/AssistantKnowledgeTab.tsx';
import AssistantSettingsTab from '../components/tabs/AssistantSettingsTab.tsx';
import AssistantSkillsTab from '../components/tabs/AssistantSkillsTab.tsx';
import { ASSISTANT_PATH, OVERVIEW_PATH } from '../constants.ts';
import { GET_AGENT, GET_AVAILABLE_SMART_INBOX_TRIGGERS } from '../queries.ts';

const TestIcon = withIcon(faFlask);

const OVERVIEW_TAB = 'overview';
const ACTIVITY_TAB = 'activity';

type NavItemKey = 'overview' | 'activity' | 'skills' | 'integrations' | 'knowledge' | 'settings';

const NAV_ITEMS: { key: NavItemKey; icon: ReturnType<typeof withIcon> }[] = [
    { key: 'overview', icon: withIcon(faGrid2) },
    { key: 'activity', icon: withIcon(faClockRotateLeft) },
    { key: 'skills', icon: withIcon(faBolt) },
    { key: 'integrations', icon: withIcon(faPlug) },
    { key: 'knowledge', icon: withIcon(faDatabase) },
    { key: 'settings', icon: withIcon(faGear) },
];

const CONFIG_TABS = {
    skills: AssistantSkillsTab,
    integrations: AssistantIntegrationsTab,
    knowledge: AssistantKnowledgeTab,
};

const AssistantPage = () => {
    const { t } = useTranslation('assistants');
    const { user } = useApplicationContext();
    // like the old Agents pages: anyone with agent access sees the activity, only managers the configuration
    const canManage = canManageAgents(user);
    const navItems = canManage ? NAV_ITEMS : NAV_ITEMS.filter(({ key }) => key === ACTIVITY_TAB);
    const { channelId = '', recordId, tab = recordId || !canManage ? ACTIVITY_TAB : OVERVIEW_TAB } = useParams();
    const [isTestDrawerOpen, setTestDrawerOpen] = useState(false);

    const { data, loading, previousData, refetch } = useQuery(GET_AGENT, {
        fetchPolicy: 'no-cache',
        notifyOnNetworkStatusChange: false,
        variables: { id: channelId },
    });
    const agent = (loading ? previousData : data)?.agent;

    const { data: triggersData, refetch: refetchTriggers } = useQuery(GET_AVAILABLE_SMART_INBOX_TRIGGERS);
    const availableSmartInboxTriggers = useMemo(() => triggersData?.availableSmartInboxTriggers || [], [triggersData]);

    if (!navItems.some(({ key }) => key === tab))
        return <Navigate to={url(ASSISTANT_PATH, { channelId, tab: canManage ? undefined : ACTIVITY_TAB })} replace />;

    const refetchAll = () => Promise.all([refetch(), refetchTriggers()]);
    const ConfigTab = tab === OVERVIEW_TAB || tab === ACTIVITY_TAB || tab === 'settings' ? null : CONFIG_TABS[tab];
    // the Activity tab brings its own content area
    const showsActivity = tab === ACTIVITY_TAB && !!agent;

    return (
        <Layout>
            <SecondaryNavigation aria-label={t('detail.navigation.label')}>
                <Page.Header className="max-md:hidden" primary={false}>
                    <Page.HeaderBreadcrumb to={url(OVERVIEW_PATH)} separator={false}>
                        {t('overview.title')}
                    </Page.HeaderBreadcrumb>
                </Page.Header>
                <SecondaryNavigation.List>
                    {navItems.map(({ key, icon: Icon }) => (
                        <SecondaryNavigation.Button key={key} asChild active={key === tab}>
                            <Link
                                to={url(ASSISTANT_PATH, { channelId, tab: key })}
                                aria-current={key === tab ? 'page' : undefined}
                            >
                                <Icon className="flex-none" />
                                {t(`detail.navigation.${key}`)}
                            </Link>
                        </SecondaryNavigation.Button>
                    ))}
                </SecondaryNavigation.List>
            </SecondaryNavigation>

            <Page className="flex-1">
                <Page.Header className="flex items-center gap-2">
                    {/* the navigation column with the back link sits below the header on small screens */}
                    <span className="flex items-center md:hidden">
                        <Page.HeaderBreadcrumb to={url(OVERVIEW_PATH)}>{t('overview.title')}</Page.HeaderBreadcrumb>
                    </span>

                    {agent && (
                        <>
                            <Page.HeaderTitle className="truncate">{agent.name}</Page.HeaderTitle>
                            {agent.enabled ? (
                                <span
                                    role="img"
                                    aria-label={t('overview.statusActive')}
                                    title={t('overview.statusActive')}
                                    className="flex"
                                >
                                    <StatusDot tone="success" />
                                </span>
                            ) : (
                                <StatusLabel tone="warning" className="text-sm">
                                    {t('overview.statusPaused')}
                                </StatusLabel>
                            )}
                        </>
                    )}

                    {showsActivity && canSeeTestingDocuments(user) && (
                        <Page.HeaderButton className="ml-auto" onClick={() => setTestDrawerOpen(true)}>
                            <TestIcon />
                            {t('detail.activity.testAssistant')}
                        </Page.HeaderButton>
                    )}
                </Page.Header>

                {showsActivity ? (
                    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
                        <AssistantActivityTab agent={agent} />
                    </div>
                ) : (
                    <Page.Content lowered>
                        {agent ? (
                            tab === 'settings' ? (
                                <AssistantSettingsTab
                                    agent={agent}
                                    onChange={refetchAll}
                                    availableSmartInboxTriggers={availableSmartInboxTriggers}
                                />
                            ) : ConfigTab ? (
                                <ConfigTab agent={agent} onChange={refetchAll} />
                            ) : (
                                <AssistantOverview
                                    agent={agent}
                                    availableSmartInboxTriggers={availableSmartInboxTriggers}
                                    onChange={refetchAll}
                                />
                            )
                        ) : loading ? (
                            <Loader />
                        ) : (
                            <p className="text-sm text-content-medium">{t('detail.notFound')}</p>
                        )}
                    </Page.Content>
                )}
            </Page>

            {agent && isTestDrawerOpen && (
                <TestAssistantDrawer agentId={agent.id} visible onClose={() => setTestDrawerOpen(false)} />
            )}
        </Layout>
    );
};

export default AssistantPage;
